// Tax Center → Settings: legal name, tax-exempt status, EIN (write-only —
// only the last 4 digits ever come back), and the address printed on
// documents.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { saveProfile, errorText } from '../../services/taxApi';

export default function TaxSettings({ profile, onSaved }) {
  const { t } = useTranslation();
  const a = profile.address || {};
  const [form, setForm] = useState({
    legalName: profile.legalName || '', exemptStatus: profile.exemptStatus || 'none', ein: '',
    line1: a.line1 || '', line2: a.line2 || '', city: a.city || '', state: a.state || '', zip: a.zip || '',
  });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setMsg(''); setErr(''); setBusy(true);
    try {
      const body = {
        legalName: form.legalName, exemptStatus: form.exemptStatus,
        address: { line1: form.line1, line2: form.line2, city: form.city, state: form.state, zip: form.zip },
      };
      if (form.ein.trim()) body.ein = form.ein.trim();
      await saveProfile(body);
      setForm((f) => ({ ...f, ein: '' }));
      setMsg(t('tax.saved', 'Saved.'));
      onSaved && onSaved();
    } catch (ex) {
      setErr(errorText(ex, t('tax.saveError', 'Could not save.')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="surface" aria-labelledby="tx-set-title">
      <h4 id="tx-set-title">{t('tax.settings.title', 'Organization tax details')}</h4>
      <form className="tx-form" onSubmit={submit}>
        <label className="fo-field">{t('tax.settings.legalName', 'Legal name')}
          <input type="text" value={form.legalName} onChange={set('legalName')} placeholder={profile.companyName} maxLength={200} />
        </label>
        <label className="fo-field">{t('tax.settings.exempt', 'Tax-exempt status')}
          <select value={form.exemptStatus} onChange={set('exemptStatus')}>
            <option value="none">{t('tax.settings.none', 'Not tax-exempt')}</option>
            <option value="501c3">{t('tax.settings.c3', '501(c)(3) public charity or private foundation')}</option>
            <option value="other_501c">{t('tax.settings.other', 'Other 501(c) (e.g. (c)(4), (c)(6), (c)(7))')}</option>
          </select>
        </label>
        <p className="tx-hint">{t('tax.settings.exemptHint', 'Giving statements and donor acknowledgments are produced only for 501(c)(3) organizations. Other exempt organizations get the fundraising & gaming summary.')}</p>
        <label className="fo-field">{t('tax.settings.ein', 'EIN')}
          <input type="text" inputMode="numeric" autoComplete="off" value={form.ein} onChange={set('ein')}
            placeholder={profile.hasEin ? t('tax.settings.einOnFile', 'On file: **-***{{last4}} — type to replace', { last4: profile.einLast4 }) : '12-3456789'} />
        </label>
        <p className="tx-hint">{t('tax.settings.einHint', 'Stored encrypted. Only the last 4 digits are shown. Printed on W-2Gs and nonprofit documents.')}</p>
        <div className="tx-row">
          <label className="fo-field">{t('tax.settings.line1', 'Address')}<input type="text" value={form.line1} onChange={set('line1')} /></label>
          <label className="fo-field">{t('tax.settings.line2', 'Address line 2')}<input type="text" value={form.line2} onChange={set('line2')} /></label>
          <label className="fo-field">{t('tax.settings.city', 'City')}<input type="text" value={form.city} onChange={set('city')} /></label>
          <label className="fo-field">{t('tax.settings.state', 'State')}<input type="text" value={form.state} onChange={set('state')} maxLength={2} /></label>
          <label className="fo-field">{t('tax.settings.zip', 'ZIP')}<input type="text" value={form.zip} onChange={set('zip')} maxLength={10} /></label>
        </div>
        <div className="tx-actions">
          <button type="submit" className="lm-btn" disabled={busy}>{t('tax.save', 'Save')}</button>
          {msg && <span className="tx-saved" role="status">{msg}</span>}
          {err && <span className="tx-err" role="alert">{err}</span>}
        </div>
      </form>
    </section>
  );
}
